<template>
  <!-- พิกัด and จุดสังเกต for one VISIT, from the planning page (จัดรถอัตโนมัติ),
       so a stop with no pin is fixed where it is noticed instead of on
       จองวันส่งสินค้า and back. The same behaviour as คิวรถเล็ก's dialog, read
       from the same rules (bookingRules parseGeo / soPin / sapPinUpdate /
       sapPinPatch), so the three pin dialogs cannot disagree:
         - SAP is read as it opens; an empty field is filled from it, a field
           that differs is offered (ใช้ค่าจาก SAP), never applied unseen.
         - Saving writes every bookingSlot of the visit (bookingService.setPin),
           the booking documents of a route already sent to คิวรถเล็ก
           (dispatchService.pinSentBookings), then SAP (sapPin) -- which never
           undoes the save and says so when it fails. -->
  <v-dialog :value="!!visit" max-width="560px" :persistent="saving" @input="v => { if (!v) close() }">
    <v-card v-if="visit">
      <v-card-title class="headline">พิกัดและจุดสังเกต</v-card-title>
      <v-card-subtitle class="pt-1">
        <div>{{ primary.name || primary.custName || '-' }}
          <span v-if="soNos.length">· SO {{ soNos.join(', ') }}</span>
          <span v-if="rtNos.length">· RT {{ rtNos.join(', ') }}</span></div>
        <div class="caption">{{ primary.address || primary.custAddress || '' }}</div>
        <div v-if="visit.length > 1" class="caption teal--text text--darken-2">
          บันทึกให้ทั้ง {{ visit.length }} การจองของลูกค้ารายนี้
        </div>
      </v-card-subtitle>
      <v-card-text>
        <v-alert v-if="fromSap" type="info" dense text class="mb-3">
          ดึงจากพิกัด/จุดสังเกตใน SAP — ตรวจสอบก่อนบันทึก
        </v-alert>
        <div v-if="loadingSap" class="caption grey--text mb-3">กำลังอ่านพิกัดจาก SAP...</div>
        <v-alert v-if="sapUpdate" type="info" dense text class="mb-3">
          <div>ข้อมูลใน SAP ไม่ตรงกับที่กรอกไว้</div>
          <div v-if="sapUpdate.geo" class="caption">พิกัดใน SAP: {{ sapUpdate.geo }}</div>
          <div v-if="sapUpdate.landmark" class="caption">จุดสังเกตใน SAP: {{ sapUpdate.landmark }}</div>
          <v-btn small text color="info" class="mt-1 px-1" @click="applySap">ใช้ค่าจาก SAP</v-btn>
        </v-alert>
        <v-form @submit.prevent="save">
          <v-text-field v-model="geo" label="พิกัด (ละติจูด, ลองจิจูด)"
                        :rules="[geoRule]" clearable color="deep-purple"
                        placeholder="13.7563, 100.5018" hide-details="auto">
            <template v-slot:append-outer>
              <v-btn small color="deep-purple" dark @click="openMap">
                <v-icon left small>mdi-map-marker</v-icon>เปิดแผนที่
              </v-btn>
            </template>
          </v-text-field>
          <v-text-field v-model="landmark" label="จุดสังเกต" clearable
                        :counter="landmarkMax" :maxlength="landmarkMax"
                        hint="เช่น บ้านหลังสีฟ้า ตรงข้ามร้านสะดวกซื้อ ปากซอยมีร้านกาแฟ"
                        persistent-hint color="deep-purple" hide-details="auto" class="mt-3" />
        </v-form>
        <div class="caption grey--text mt-3">บันทึกแล้วจะวางแผนใหม่ทันที</div>
        <div v-if="error" class="error--text mt-2">{{ error }}</div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="saving" @click="close">ยกเลิก</v-btn>
        <v-btn color="deep-purple" dark :loading="saving" @click="save">บันทึก</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'
import R from '@/components/bookingRules'
import S from '@/components/bookingService'
import RJ from '@/components/returnJob'
import D from '@/components/dispatchService'
import { pushPinToSap, pushRtPinToSap, sapFailedNote } from '@/components/sapPin'
import { reportError, reportWarning } from '@/components/report'

export default {
  name: 'pinDialog',
  props: {
    // The bookingSlot rows of one visit, or null when closed.
    visit: { type: Array, default: null },
    date: { type: String, default: '' }
  },
  data () {
    return {
      geo: '',
      landmark: '',
      fromSap: false,
      sapPin: null,
      loadingSap: false,
      saving: false,
      error: '',
      landmarkMax: R.SAP_LANDMARK_MAX
    }
  },
  computed: {
    webPath () { return this.$store.getters.webPath },
    user () { return this.$store.state.user || '' },
    // The booking whose pin the visit already shows, else the first.
    primary () {
      const v = this.visit || []
      return v.find(b => R.parseGeo(b.geo)) || v.find(b => b.landmark) || v[0] || {}
    },
    // Where the visit's pin goes in SAP: SOs (an exchange's too), and a pickup's RT.
    pinTo () { return RJ.pinNumbers(this.visit || []) },
    soNos () { return this.pinTo.soNos },
    rtNos () { return this.pinTo.rtNos },
    sapUpdate () { return R.sapPinUpdate({ geo: this.geo, landmark: this.landmark }, this.sapPin) }
  },
  watch: {
    visit: {
      immediate: true,
      handler (v) { if (v) this.start(v) }
    }
  },
  methods: {
    start (visit) {
      this.geo = this.primary.geo || ''
      this.landmark = this.primary.landmark || ''
      this.fromSap = false
      this.sapPin = null
      this.error = ''
      this.saving = false
      this.loadSap(visit)
    },
    // U_KSK_Geolocation / U_KSK_Landmark of the visit's first SO. Advisory: a
    // lookup that failed leaves the dialog usable by hand.
    async loadSap (visit) {
      const so = this.soNos[0]
      if (!so) return
      this.loadingSap = true
      try {
        const res = await axios.post(`${this.webPath}deliveryWebService.asmx/GetSo`,
          { so: Number(so) }, { timeout: 10000 })
        const rows = JSON.parse(res.data.d)
        const row = Array.isArray(rows) && rows.length ? rows[0] : null
        // Closed, or opened on another visit, while SAP answered.
        if (!row || this.visit !== visit) return
        const sap = R.soPin(row)
        if (sap.geo && !this.geo) { this.geo = sap.geo; this.fromSap = true }
        if (sap.landmark && !this.landmark) { this.landmark = sap.landmark; this.fromSap = true }
        this.sapPin = sap
      } catch (e) {
        reportWarning('Reading พิกัด from SAP failed:', e)
      } finally {
        if (this.visit === visit) this.loadingSap = false
      }
    },
    applySap () {
      const update = this.sapUpdate
      if (!update) return
      if (update.geo !== undefined) this.geo = update.geo
      if (update.landmark !== undefined) this.landmark = update.landmark
      this.fromSap = true
    },
    geoRule (v) {
      return !v || !!R.parseGeo(v) || 'รูปแบบพิกัดไม่ถูกต้อง เช่น 13.7563, 100.5018'
    },
    openMap () {
      const point = R.parseGeo(this.geo)
      const b = this.primary
      const query = point
        ? R.formatGeo(point)
        : [b.address || b.custAddress, (b.location || '').split(',').filter(Boolean).join(' '), b.zipcode]
          .filter(Boolean).join(' ').trim()
      const url = query
        ? 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(query)
        : 'https://www.google.com/maps'
      window.open(url, '_blank', 'noopener')
    },
    close () {
      if (this.saving) return
      this.$emit('close')
    },
    async save () {
      const raw = (this.geo || '').trim()
      const point = R.parseGeo(raw)
      // Refused, not dropped: a half-parsed coordinate sends a driver somewhere
      // confidently wrong.
      if (raw && !point) {
        this.error = 'รูปแบบพิกัดไม่ถูกต้อง เช่น 13.7563, 100.5018'
        return
      }
      const fields = { geo: point ? R.formatGeo(point) : '', landmark: (this.landmark || '').trim() }
      // Captured now: the page closes the dialog on 'saved', which empties
      // `visit` and everything computed from it before SAP is written.
      const visit = this.visit
      const soNos = this.soNos
      const rtNos = this.rtNos
      const sapBefore = this.sapPin || this.primary
      this.saving = true
      this.error = ''
      let changed = 0
      try {
        for (const b of visit) {
          const changes = await S.setPin(b.id, { geo: fields.geo, landmark: fields.landmark, user: this.user })
          if (changes.length) changed++
        }
      } catch (e) {
        reportError('Saving พิกัด failed:', e)
        this.error = 'บันทึกไม่สำเร็จ โปรดลองอีกครั้ง'
        this.saving = false
        return
      }

      // A route already sent to คิวรถเล็ก: its booking documents hold their
      // own copy. A failure here is reported, not fatal -- the booking is saved.
      let sentNote = ''
      try {
        await D.pinSentBookings(this.date, visit.map(b => b.id), fields, this.user)
      } catch (e) {
        reportError('Updating sent bookings with พิกัด failed:', e)
        sentNote = 'บันทึกที่การจองแล้ว แต่ยังอัปเดตงานในคิวรถเล็กไม่สำเร็จ โปรดแก้ที่หน้าคิวรถเล็ก'
      }

      this.saving = false
      this.$emit('saved', { changed: changed })

      // Then SAP, after Firestore and never undoing it. Only what changed,
      // measured against what SAP answered when the dialog opened.
      const patch = R.sapPinPatch(sapBefore, fields)
      const failed = (await pushPinToSap(this.webPath, soNos, patch)).concat(await pushRtPinToSap(this.webPath, rtNos, patch))
      const notes = [sentNote, failed.length ? sapFailedNote(failed) : ''].filter(Boolean)
      if (notes.length) {
        this.$root.$message('บันทึกพิกัดแล้ว', notes, { type: 'alert', color: 'warning', width: 450 })
      }
    }
  }
}
</script>
